import { defineStore } from 'pinia'
import { ref } from 'vue'
import { notificationApi, type NotificationItem as ApiNotificationItem } from '@/api/notification'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

// 配置 dayjs
dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

export interface NotificationItem {
    id: string
    title: string
    content: string
    time: string
    type: 'gift' | 'compensation' | 'payment' | 'system' | 'achievement'
    read: boolean
    displayLevel: 'HIGH' | 'NORMAL'
    data?: any
    status?: 'pending' | 'success' | 'failed'
}

export const useNotificationStore = defineStore('notification', () => {
    const notifications = ref<NotificationItem[]>([])
    const unreadCount = ref(0)
    const loading = ref(false)
    let pollingTimer: number | null = null
    let isInitialized = false // 跟踪是否已初始化

    // 格式化时间为相对时间
    const formatTime = (dateString: string): string => {
        try {
            return dayjs(dateString).fromNow()
        } catch {
            return dateString
        }
    }

    // 转换 API 数据为 Store 数据格式
    const convertApiNotification = (apiNotif: ApiNotificationItem): NotificationItem => {
        return {
            id: String(apiNotif.id),
            title: apiNotif.title,
            content: apiNotif.content,
            time: formatTime(apiNotif.createdAt),
            type: apiNotif.type,
            read: apiNotif.isRead,
            displayLevel: apiNotif.displayLevel,
            data: apiNotif.data,
            status: apiNotif.status
        }
    }

    // 获取通知列表
    const fetchNotifications = async (params?: { page?: number; pageSize?: number; type?: string; isRead?: boolean }) => {
        try {
            loading.value = true
            const response = await notificationApi.getList(params)
            notifications.value = response.list.map(convertApiNotification)
            return response
        } catch (error) {
            console.error('获取通知列表失败:', error)
            throw error
        } finally {
            loading.value = false
        }
    }

    // 获取未读数量
    const fetchUnreadCount = async () => {
        try {
            const response = await notificationApi.getUnreadCount()
            unreadCount.value = response.count
        } catch (error) {
            console.error('获取未读数量失败:', error)
        }
    }

    // 标记为已读
    const markAsRead = async (id: string) => {
        try {
            await notificationApi.markAsRead(Number(id))

            // 更新本地状态
            const notification = notifications.value.find(n => n.id === id)
            if (notification) {
                notification.read = true
            }

            // 更新未读数量
            unreadCount.value = Math.max(0, unreadCount.value - 1)
        } catch (error) {
            console.error('标记已读失败:', error)
            throw error
        }
    }

    // 标记所有为已读
    const markAllAsRead = async () => {
        try {
            await notificationApi.markAllAsRead()

            // 更新本地状态
            notifications.value.forEach(n => n.read = true)
            unreadCount.value = 0
        } catch (error) {
            console.error('标记所有已读失败:', error)
            throw error
        }
    }

    // 删除通知
    const deleteNotification = async (id: string) => {
        try {
            await notificationApi.delete(Number(id))

            // 从本地列表中移除
            const index = notifications.value.findIndex(n => n.id === id)
            if (index !== -1) {
                const notification = notifications.value[index]
                if (!notification.read) {
                    unreadCount.value = Math.max(0, unreadCount.value - 1)
                }
                notifications.value.splice(index, 1)
            }
        } catch (error) {
            console.error('删除通知失败:', error)
            throw error
        }
    }

    // 清空已读通知（调用后端API）
    const clearReadNotifications = async () => {
        try {
            const response = await notificationApi.clearRead()

            // 从本地列表中移除已读通知
            notifications.value = notifications.value.filter(n => !n.read)

            return response
        } catch (error) {
            console.error('清空已读通知失败:', error)
            throw error
        }
    }

    // 触发支付自助修复（保留原有逻辑）
    const triggerSelfRepair = async (id: string) => {
        const notification = notifications.value.find(n => n.id === id)
        if (!notification) return

        // Simulate API call
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                notification.status = 'success'
                notification.title = '支付确认成功'
                notification.content = '已到账 (自助修复)'
                notification.read = false
                resolve()
            }, 1500)
        })
    }

    // 开始轮询（每30秒）
    const startPolling = () => {
        // 立即执行一次
        fetchUnreadCount()

        // 设置定时器
        if (pollingTimer) {
            clearInterval(pollingTimer)
        }

        pollingTimer = window.setInterval(() => {
            fetchUnreadCount()
        }, 30000) // 30秒
    }

    // 停止轮询
    const stopPolling = () => {
        if (pollingTimer) {
            clearInterval(pollingTimer)
            pollingTimer = null
        }
    }

    // 页面可见性变化时的处理
    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            // 页面变为可见时，立即拉取一次
            fetchUnreadCount()
        }
    }

    // 初始化（在用户登录后调用）
    const initialize = () => {
        // 幂等性检查：避免重复初始化
        if (isInitialized) {
            return
        }

        startPolling()

        // 监听页面可见性变化
        document.addEventListener('visibilitychange', handleVisibilityChange)

        isInitialized = true
    }

    // 清理（在用户登出时调用）
    const cleanup = () => {
        stopPolling()
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        notifications.value = []
        unreadCount.value = 0
        isInitialized = false // 重置初始化标志
    }

    return {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearReadNotifications,
        triggerSelfRepair,
        startPolling,
        stopPolling,
        initialize,
        cleanup
    }
})
